import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { api } from '@/api/Api';
import { RolesData } from '@/interface';
import { StateReducer } from '@/redux/modules/reducer';
import { selectDepartmentList } from '@/redux/modules/department/departmentSelectors';
import { hasAnyRole } from '@/core/helpers/PageHelpers';
import { calcCharterCapital, CapitalGroup, CapitalRates, parseGoldPrice } from '@/core/helpers/charterCapital';

const CHARTER_CAPITAL_ROLES: (keyof RolesData)[] = ["isDirector", "isAccountant", "isAdmin"];
const RATES_REFRESH_MS = 60 * 60 * 1000;

/**
 * Уставной капитал по юрлицам для директора, бухгалтера и админа.
 * Точки — из selectDepartmentList, он уже ограничен ролью: директор франшизы
 * видит только точки своего тенанта (franchiseTenantId).
 * Балансы берутся из стора (их обновляет ratesHub), курсы НБ РК — раз в час.
 * Пока курсы не пришли, ничего не считаем: иначе по нашим курсам покупки
 * могла бы мигнуть ложная недостача.
 */
export const useCharterCapital = (): CapitalGroup[] => {
    const rolesData = useSelector((state: StateReducer) => state?.header?.rolesData);
    const departmentList = useSelector(selectDepartmentList);
    const enabled = hasAnyRole(rolesData, CHARTER_CAPITAL_ROLES);
    const [rates, setRates] = useState<CapitalRates | null>(null);

    useEffect(() => {
        if (!enabled) {
            setRates(null);
            return;
        }

        let cancelled = false;

        const loadRates = async () => {
            const [nbRes, goldRes] = await Promise.allSettled([
                api.parsing.loadNationalRates(),
                api.parsing.loadNationalGoldRate(),
            ]);

            if (cancelled) return;

            // Курс не загрузился — пересчитаем по нашей покупке, как и страница казначея
            setRates({
                nbRates: nbRes.status === "fulfilled" ? nbRes.value?.rates?.item || [] : [],
                goldPrice: goldRes.status === "fulfilled" ? parseGoldPrice(goldRes.value?.price) : 0,
            });
        };

        loadRates();
        const id = setInterval(loadRates, RATES_REFRESH_MS);

        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [enabled]);

    return useMemo(
        () => (enabled && rates && departmentList?.length ? calcCharterCapital(departmentList, rates) : []),
        [enabled, rates, departmentList]
    );
};
